import {assert} from 'assert-ts';
import type {Schemas} from 'domainTypes/dto';
import {Concept} from 'types';
import {
  mapDateTypeDto,
  mapToDateTypeDto,
} from 'services/agent/mappers/mapDateTypeDto';
import {
  mapAgentCorporationRelationsDto,
  mapAgentPersonRelationsDto,
  mapToAgentCorporationRelationsDto,
  mapToAgentPersonRelationsDto,
} from 'services/agent/mappers/mapRelationsDto';
import {mapLiteraturePrizeLink, mapLiteraturePrizeWork} from 'services/prizes';
import type {
  Agent,
  AgentCorporation,
  AgentEvent,
  AgentPerson,
  AgentPrizeWon,
  AgentRoles,
} from '../types';
import {mapToAgentTypeDto} from './mapAgentTypeDto';
import {
  mapNameVariantsDto,
  mapNameVariantsWithDescriptionDto,
  mapToNameVariantsDto,
  mapToNameVariantsDtoAndDescription,
} from './mapNameVariantsDto';

export const mapAgentDto = (
  dto:
    | Schemas.AgentPersonDto
    | Schemas.AgentCorporationDto
    | Schemas.AgentEventDto
    | Schemas.AgentPublisherDto,
): Agent => {
  const agentType = dto.agentType;
  //--- Corporation ---
  if (agentType === 'CORPORATION') {
    const corpDto = dto as Schemas.AgentCorporationDto;
    const {
      nameVariants,
      description,
      agentCorporationRelations,
      workRelations,
      works,
      expressions,
      prizes,
      ...restDto
    } = corpDto;

    const links =
      mapAgentCorporationRelationsDto({
        agentCorporationRelations,
        workRelations,
      }) ?? [];
    return {
      ...restDto,
      agentType: Concept.corporation,
      nameVariants: mapNameVariantsWithDescriptionDto(
        nameVariants,
        description,
      ),
      roles: mergeWorkRolesAndExpressionRoles(works, expressions),
      links: links.length > 0 ? links : undefined,
      prizes: mapAgentPrizesWonDto(prizes),
      // subsidiary has its own column in corporation search
      subsidiary: (corpDto.nameVariants ?? [])
        .map(n => n.subsidiary)
        .filter(a => a) as string[],
    };
  }
  //--- Event ---
  else if (agentType === 'EVENT') {
    const corpDto = dto as Schemas.AgentEventDto;
    const {
      nameVariants,
      description,
      // workRelations,
      works,
      expressions,
      prizes,
      ...restDto
    } = corpDto;

    return {
      ...restDto,
      agentType: Concept.event,
      nameVariants: mapNameVariantsWithDescriptionDto(
        nameVariants,
        description,
      ),
      roles: mergeWorkRolesAndExpressionRoles(works, expressions),
      prizes: mapAgentPrizesWonDto(prizes),
    };
  }
  //--- Person ---
  else if (agentType === 'PERSON') {
    const personDto = dto as Schemas.AgentPersonDto;
    const {
      nameVariants,
      birth,
      death,
      agentPersonRelations,
      workRelations,
      works,
      expressions,
      prizes,
      occupationalField,
      ...restDto
    } = personDto;

    return {
      ...restDto,
      agentType: Concept.person,
      nameVariants: mapNameVariantsDto(nameVariants),
      birth: mapDateTypeDto(birth),
      death: mapDateTypeDto(death),
      roles: mergeWorkRolesAndExpressionRoles(works, expressions),
      links: mapAgentPersonRelationsDto({agentPersonRelations, workRelations}),
      prizes: mapAgentPrizesWonDto(prizes),
      occupationalField:
        !Array.isArray(occupationalField) || occupationalField.length === 0
          ? ['']
          : occupationalField,
    };
  }
  //--- Publisher ---
  else {
    const {
      nameVariants,
      // workRelations,
      works,
      expressions,
      prizes,
      ...restDto
    } = dto;
    return {
      ...restDto,
      agentType: Concept.publisher,
      nameVariants: mapNameVariantsDto(nameVariants),
      roles: mergeWorkRolesAndExpressionRoles(works, expressions),
      prizes: mapAgentPrizesWonDto(prizes),
    };
  }
};

const mapAgentPrizesWonDto = (
  dto: Schemas.AgentPrizeWon[] | undefined,
): AgentPrizeWon[] => {
  return (dto ?? [])
    .map(mapAgentPrizeWonDto)
    .sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
};

export const mapAgentPrizeWonDto = ({
  prize: loosePrize,
  work: looseWork,
  type: looseType,
  year: looseYear,
  ...dto
}: Schemas.AgentPrizeWon): AgentPrizeWon => {
  const prize = assert(
    loosePrize,
    'mapAgentPrizeWonDto: prize must be defined',
  );
  const type = assert(looseType, 'mapAgentPrizeWonDto: type must be defined');
  const year = assert(looseYear, 'mapAgentPrizeWonDto: year must be defined');

  assert.soft(
    type === 'AUTHORSHIP' || looseWork,
    'mapAgentPrizeWonDto: work must be defined when not AUTHORSHIP',
  );

  return {
    ...dto,
    year,
    type,
    prize: {link: mapLiteraturePrizeLink(prize)},
    work: looseWork ? {link: mapLiteraturePrizeWork(looseWork)} : undefined,
  };
};

/**
 * Strip id, userId, created and modified
 */
export const mapToPostPutAgentDto = (
  _method: 'POST' | 'PUT',
  agent: Agent,
  internalAgent: boolean,
):
  | Schemas.AgentPersonDto
  | Schemas.AgentCorporationDto
  | Schemas.AgentPublisherDto
  | Schemas.AgentEventDto => {
  //--- Corporation ---
  if (agent.agentType === Concept.corporation) {
    const corpAgent = agent as AgentCorporation;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id: _id,
      agentType,
      nameVariants,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      roles: _works,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      prizes,
      links,
      ...data
    } = corpAgent;
    const relations = mapToAgentCorporationRelationsDto(links);

    return {
      ...data,
      agentType: mapToAgentTypeDto(agentType),
      ...mapToNameVariantsDtoAndDescription(nameVariants),
      ...relations,
      internalAgent,
    };
  }
  //--- Event ---
  else if (agent.agentType === Concept.event) {
    const eventAgent = agent as AgentEvent;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id: _id,
      agentType,
      nameVariants,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      roles: _works,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      prizes,
      ...data
    } = eventAgent;
    return {
      ...data,
      agentType: mapToAgentTypeDto(agentType),
      ...mapToNameVariantsDtoAndDescription(nameVariants),
      internalAgent,
    };
  }
  //--- Person ---
  else if (agent.agentType === Concept.person) {
    const personAgent = agent as AgentPerson;

    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id: _id,
      agentType,
      nameVariants,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      roles: _works,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      prizes,
      links,
      birth,
      death,
      occupationalField,
      ...data
    } = personAgent;

    const relations = mapToAgentPersonRelationsDto(links);

    // We pre-add first item so users don't have to add it themselves.
    const occupationalFieldNotEmpty = occupationalField
      ? occupationalField.filter(o => o.trim() !== '')
      : [];

    return {
      ...data,
      birth: mapToDateTypeDto(birth),
      death: mapToDateTypeDto(death),
      agentType: mapToAgentTypeDto(agentType),
      nameVariants: mapToNameVariantsDto(nameVariants),
      occupationalField:
        occupationalFieldNotEmpty.length > 0
          ? occupationalFieldNotEmpty
          : undefined,
      ...relations,
      internalAgent,
    };
  }
  //--- Publisher ---
  else {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      id: _id,
      agentType,
      nameVariants,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      roles: _works,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      prizes,
      ...data
    } = agent;

    return {
      ...data,
      agentType: mapToAgentTypeDto(agentType),
      nameVariants: mapToNameVariantsDto(nameVariants),
      internalAgent,
    };
  }
};

const mergeWorkRolesAndExpressionRoles = (
  works?: Schemas.AgentWorkRoleDto[],
  expressions?: Schemas.AgentExpressionRoleDto[],
): AgentRoles[] => {
  const agentRoles: AgentRoles[] = [];
  (works ?? []).forEach(work => {
    const exists = agentRoles.findIndex(
      roles => roles.workId === work.entityId,
    );

    if (exists !== -1) {
      work.roles.forEach(role => {
        if (!agentRoles[exists].roles.includes(role)) {
          agentRoles[exists].roles.push(role);
        }
      });
    } else {
      agentRoles.push({
        workId: work.entityId,
        roles: work.roles,
      });
    }
  });

  (expressions ?? []).forEach(expression => {
    const exists = agentRoles.findIndex(
      roles => roles.workId === expression.workId,
    );
    if (exists !== -1) {
      expression.roles.forEach(role => {
        if (!agentRoles[exists].roles.includes(role)) {
          agentRoles[exists].roles.push(role);
        }
      });
    } else {
      agentRoles.push({
        workId: expression.workId,
        roles: expression.roles,
      });
    }
  });

  return agentRoles;
};
